/**
 * Einstellungen des CRM — die Tenant-Config (admin-only).
 *
 * Reiter: Branding, Kontakt, Rechtliches, Features, Vorlagen. Was am
 * einzelnen Menschen haengt, steht nicht hier, sondern im Profil auf
 * konto.elbklar.com (ADR-0005).
 */

import { useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { AmRechnerBearbeiten, Card, EmptyState, Skeleton, Tabs, TabsContent } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { BrandingForm } from './components/BrandingForm';
import { ContactForm } from './components/ContactForm';
import { LegalForm } from './components/LegalForm';
import { FeatureFlagsPanel } from './components/FeatureFlagsPanel';
import { TemplateEditor } from './components/TemplateEditor';

type TabId = 'branding' | 'contact' | 'legal' | 'features' | 'templates';

const tabs = [
  { id: 'branding' as TabId, label: 'Branding' },
  { id: 'contact' as TabId, label: 'Kontakt' },
  { id: 'legal' as TabId, label: 'Rechtliches' },
  { id: 'features' as TabId, label: 'Funktionen' },
  { id: 'templates' as TabId, label: 'Vorlagen' },
];

const UNTERTITEL = 'Was für das ganze Haus gilt: Branding, Kontakt, Rechtliches, Vorlagen. Persönliches liegt im Konto.';

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('branding');
  const { data: config, isLoading, isError, error } = useTenantConfig();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <CrmPageHeader title="Einstellungen" subtitle={UNTERTITEL} />
        <Skeleton className="h-96 w-full rounded-lg" />
      </div>
    );
  }

  if (isError) {
    // Der Hook meldet fehlende Rechte explizit (403); alles andere ist ein
    // technischer Fehler und hat mit Berechtigungen nichts zu tun.
    const meldung = (error as Error)?.message || 'Fehler beim Laden der Einstellungen';
    const fehlendeBerechtigung = meldung.includes('Berechtigung');

    return (
      <div className="space-y-6">
        <CrmPageHeader title="Einstellungen" />
        <Card>
          <EmptyState
            icon={CircleAlert}
            title={meldung}
            description={fehlendeBerechtigung ? 'Diese Seite ist Administratoren vorbehalten.' : undefined}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CrmPageHeader title="Einstellungen" subtitle={UNTERTITEL}>
        {/* Bearbeitet wird am Rechner (M2); am Handy steht darunter eine Zusammenfassung. */}
        <div className="w-full max-md:hidden">
        <Tabs
          className="w-full"
          label="Bereiche der Einstellungen"
          value={activeTab}
          onChange={(v) => setActiveTab(v as TabId)}
          items={tabs.map((tab) => ({ value: tab.id, label: tab.label }))}
        >
          <Card className="mt-2">
            <TabsContent value="branding" className="pt-0">{config && <BrandingForm config={config} />}</TabsContent>
            <TabsContent value="contact" className="pt-0">{config && <ContactForm config={config} />}</TabsContent>
            <TabsContent value="legal" className="pt-0">{config && <LegalForm config={config} />}</TabsContent>
            <TabsContent value="features" className="pt-0">
              <FeatureFlagsPanel />
            </TabsContent>
            <TabsContent value="templates" className="pt-0">{config && <TemplateEditor config={config} />}</TabsContent>
          </Card>
        </Tabs>
        </div>
      </CrmPageHeader>

      {config && (
        <div className="space-y-4 md:hidden">
          <AmRechnerBearbeiten>Branding, Kontakt, Rechtliches, Funktionen und Vorlagen lassen sich am Rechner ändern.</AmRechnerBearbeiten>
          <Card title="Zusammenfassung">
            <dl className="space-y-3">
              {[
                ['Firma', config.company_name],
                ['Anschrift', [config.street, [config.postal_code, config.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')],
                ['E-Mail', config.email],
                ['Telefon', config.phone],
                ['Website', config.website],
                ['Firmierung', config.legal_name],
                ['Register', [config.registry_court, config.registry_number].filter(Boolean).join(', ')],
                ['USt-IdNr.', config.vat_id],
                ['Geschäftsführung', config.managing_directors],
              ]
                .filter(([, wert]) => wert)
                .map(([label, wert]) => (
                  <div key={label}>
                    <dt className="t-label text-muted-foreground">{label}</dt>
                    <dd className="break-words">{wert}</dd>
                  </div>
                ))}
            </dl>
          </Card>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
